import { Component, ElementRef, ViewChild, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractControl, FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { environment } from "../../../../environments/environment";
import { ExternalService } from 'src/app/Services/external.service';
import { UtilsService } from 'src/app/Services/utils.service';
import { GeolocationService } from 'src/app/Services/geolocation.service';
import { BsDatepickerConfig } from "ngx-bootstrap/datepicker";
import { CmsService } from 'src/app/Services/cms.service';
import { Scroller } from 'primeng/scroller';

import { PDFDocument, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';

import { Map as MapboxMap, LngLatBounds, GeoJSONSource } from 'mapbox-gl';
import * as mapboxgl from 'mapbox-gl';

interface Stop {
  id: string;
  code: string;
  name: string;
  municipality: string;
  lat: number;
  lng: number;
  zone: string;
}

interface MarkerStyle {
  src: string;
  width: string;
  height: string;
  offset: [number, number];
  zIndex: number;
}

@Component({
  selector: 'app-paragens',
  templateUrl: './paragens.component.html',
  standalone: false
})

export class ParagensComponent implements OnInit{
  @ViewChild('TabsContentRef') TabsContentRef!:Scroller
  @ViewChild('targetSection', { static: false }) targetSection!: ElementRef;
  map!: MapboxMap;

  latitude?: number;
  longitude?: number;
  error?: string;

  stopFromUrl: string = '';

  // DatePicker ngx-bootstrap
  bsValue = new Date();
  bsConfig: Partial<BsDatepickerConfig> = {};
  minDate  = new Date();
  maxDate = new Date();
  municipalities: any[] = [];

  // Map Style && Options
  zoom = environment.mapZoom;
  initialZoom = environment.mapZoom;
  lastZoomLevel: number | null = null;
  center_orig: google.maps.LatLngLiteral = {
    lat: environment.mapCenter.lat,
    lng: environment.mapCenter.lon,
  };
  center: google.maps.LatLngLiteral = {
    lat: environment.mapCenter.lat,
    lng: environment.mapCenter.lon,
  };
  
  public markerStyleSelected: MarkerStyle = {
    src: 'assets/img/map/1x/stop-icn-selected2.png',
    width: '47px',
    height: '80px',
    offset: [23, 0],
    zIndex: 1
  }
  // End Map Style && Options

  searchForm: FormGroup = new FormGroup({
    municipality: new FormControl(''),
    text: new FormControl('')
  });
  ignoreChanges: boolean = false;

  searching: boolean = false;
  searchingSchedule: boolean = false;

  stops: any[] = []
  stopsFiltered: any[] = [];
  stopsFilteredMap: any[] = [];
  results: any[] = [];
  stopSchedule: any[] = [];
  stopSchedulePastTrue: any[] = [];
  stopSchedulePastFalse: any[] = [];
  selectedStop: string = '';
  selectedStopText: string = '';
  stringZone: string = '';
  day: string = '';
  isToday: boolean = false;
  showPast: boolean = false;
  showButton: boolean = true;
  showMyHour: boolean = true;

  allRoutes: any[] = [];
  hora: number = 0;
  minutos: number = 0;
  today: string = '';
  tomorrow: string = '';
  selectedDay: string = '';
  loadingStops: number = 0;
  loadingStopInfo: number = 0;
  activeButton: string = 'today';
  private isInitialLoad = true;

  // ---CMS---
  language: string = '';
  defaultLanguage: string = environment.defaultLanguage;
  stops_content: string = 'linhas-paragen';
  stopsInfo: any = {};

  // ---Mapbox---
  bounds: LngLatBounds | undefined;
  selectedStopData: any = null;

  currentStyle: string = 'streets';
  mapStyle: string = 'mapbox://styles/mapbox/streets-v12';

  constructor(
    private externalService: ExternalService,
    private utilsService: UtilsService,
    private formBuilder: FormBuilder,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private cmsService: CmsService,
    private translate: TranslateService,
    private title: Title,
    private geolocationService: GeolocationService
  ) {
    this.setDateRange();
    this.configureDatepicker();
    let lang = localStorage.getItem('language') ?? this.defaultLanguage;
    this.language = lang.split('-')[0];

    this.translate.get('navLinks').pipe().subscribe(trans => {
      let pageTitle =  trans['linhas'] + ' | ' + trans['paragens']
      this.title.setTitle(pageTitle);
    })

    this.translate.get('stops').pipe().subscribe(trans => {
      this.stringZone =  trans['zona']
    })
  }

  ngOnInit() {
    this.activatedRoute.queryParams.subscribe(queryParams => {
      this.stopFromUrl = queryParams['paragem'] ? '1:' + queryParams['paragem'] : ''
    })

    this.updateRealTime();

    this.getUserLocation();

    this.searchForm = this.formBuilder.group(
      {
        municipality: [''],
        text: ['']
      },
    );
    
    this.getContent();
    this.onChanges();
    this.getRoutes();
    this.activeButton = 'today';
    this.selectedDay = this.utilsService.getTodayDate();
    this.today = this.utilsService.getTodayDate();
    this.tomorrow = this.utilsService.getTomorrowDate();
    setTimeout(() => {
      this.changeDateToday('today');
      this.searchingSchedule = false
    });

  }

  async generatePdf() {
    let orientation = 'v'; // 'h' or 'v'
    const existingPdfBytes = await fetch(`/assets/templates/template_${orientation}.pdf`).then(res => res.arrayBuffer());
    const existingPdfDoc = await PDFDocument.load(existingPdfBytes);
    const pdfDoc = await PDFDocument.create();
    pdfDoc.registerFontkit(fontkit);

    const [templatePage] = await existingPdfDoc.copyPages(existingPdfDoc, [0]);
    const embeddedTemplate = await pdfDoc.embedPage(templatePage);

    const fontBytes = await fetch('/assets/fonts/Urbanist-Regular.ttf').then(res => res.arrayBuffer());
    const font = await pdfDoc.embedFont(fontBytes);
    const fontBytesBold = await fetch('/assets/fonts/Urbanist-Bold.ttf').then(res => res.arrayBuffer());
    const fontBold = await pdfDoc.embedFont(fontBytesBold);

    const fontSize = 10;
    const fontSizeHeader = 16;
    const fontSizeTableHeader = 12;
    const fontSizeFooter = 8;

    const margin = 50;
    const topMargin = 100;
    const bottomMargin = 70;
    const colWidths = orientation === 'v' ? [50, 30, 120, 300, 1] : [50, 30, 220, 400, 1];
    const headers = ['Horário', 'N.', 'Linha', 'Destino',''];
    const rowPadding = 4;

    const rows = this.stopSchedule.map(line => [
      line.realtimeArrivalDesc || '',
      `${line.routeShortName || ''}`.trim(),
      `${line.routeLongName || ''}`.trim(),
      line.headsign || '',
      line.routeColor || ''
    ]);

    const stopName = this.selectedStopText;
    const day = this.day;

    function drawHeader(page: any, pageIndex: number) {
      page.drawText(stopName, {
        x: margin,
        y: page.getHeight() - 35,
        size: fontSizeHeader,
        font: fontBold,
        color: rgb(1, 1, 1),
      });
    }

    function drawFooter(page: any, pageIndex: number, totalPages: number) {
      const pageWidth = page.getWidth();
      const footerText = `Página ${pageIndex + 1} de ${totalPages}`;
      page.drawText(footerText, {
        x: pageWidth - margin - font.widthOfTextAtSize(footerText, fontSize),
        y: bottomMargin - 15,
        size: fontSizeFooter,
        font,
        color: rgb(0, 0, 0),
      });

      const line1 = `Horário para o dia ${day}. Sujeito a alterações sem aviso prévio.`;
      const line2 = `Para mais informações, ligue para o número 255 718 347 ou consulte o website https://linhasdourotamegaesousa.pt.`;

      page.drawText(line1, {
        x: margin,
        y: 20,
        size: fontSizeFooter,
        font,
        color: rgb(1, 1, 1),
      });

      page.drawText(line2, {
        x: margin,
        y: 10,
        size: fontSizeFooter,
        font,
        color: rgb(1, 1, 1),
      });
    }

    function wrapTextToWidth(text: string, font: any, fontSize: number, maxWidth: number): string[] {
      const words = text.split(' ');
      const lines: string[] = [];
      let currentLine = '';

      for (const word of words) {
        const testLine = currentLine ? currentLine + ' ' + word : word;
        const width = font.widthOfTextAtSize(testLine, fontSize);
        if (width <= maxWidth) {
          currentLine = testLine;
        } else {
          if (currentLine) lines.push(currentLine);
          currentLine = word;
        }
      }
      if (currentLine) lines.push(currentLine);
      return lines;
    }

    function getRowHeight(row: string[], isHeader = false): number {
      const size = isHeader ? fontSizeTableHeader : fontSize;
      const wrapped = row.map((cell, i) =>
        wrapTextToWidth(cell, font, size, colWidths[i] - rowPadding * 2)
      );
      const maxLines = Math.max(...wrapped.map(lines => lines.length));
      return maxLines * (size + 2) + rowPadding * 2;
    }

    function hexToRgbNormalized(hex: string): { r: number, g: number, b: number } {
      const hexClean = hex.replace('#', '');
      const bigint = parseInt(hexClean, 16);
      const r = ((bigint >> 16) & 255) / 255;
      const g = ((bigint >> 8) & 255) / 255;
      const b = (bigint & 255) / 255;
      return { r, g, b };
    }

    function drawStyledRow(row: string[], page: any, y: number, isHeader = false, isOdd = false): number {

      let x = margin;
      const size = isHeader ? fontSizeTableHeader : fontSize;
      const bgColor = isHeader
        ? rgb(0.8, 0.8, 0.8)
        : isOdd
        ? rgb(0.95, 0.95, 0.95)
        : rgb(1, 1, 1);

      const wrapped = row.map((cell, i) =>
        wrapTextToWidth(cell, font, size, colWidths[i] - rowPadding * 2)
      );
      const rowHeight = Math.max(...wrapped.map(lines => lines.length)) * (size + 2) + rowPadding * 2;
      const roundedRowHeight = Math.round(rowHeight);
      const roundedY = Math.round(y);

      const { r, g, b } = hexToRgbNormalized(row[4]);
      const lineColor = rgb(r, g, b);

      for (let i = 0; i < row.length - 1; i++) {
        page.drawRectangle({
          x,
          y: roundedY - roundedRowHeight + rowPadding / 2,
          width: colWidths[i],
          height: roundedRowHeight,
          color: (i == 1 && !isHeader) ? lineColor : bgColor,
          borderColor: rgb(0.7, 0.7, 0.7),
          borderWidth: 1,
        });

        const cellLineHeight = size + 2;
        const cellTotalHeight = wrapped[i].length * cellLineHeight;
        const cellStartY = roundedY - ((rowHeight - cellTotalHeight) / 2) - size;

        wrapped[i].forEach((line, lineIndex) => {
          const textWidth = font.widthOfTextAtSize(line, size);
          
          let textX = x + rowPadding; // Default: left-align
          if (i < 2) { // center columns 0 and 1
            textX = x + (colWidths[i] - textWidth) / 2;
          }

          page.drawText(line, {
            x: textX,
            y: cellStartY - lineIndex * cellLineHeight,
            size,
            font: (isHeader || i < 2) ? fontBold : font,
            color: rgb(0, 0, 0),
          });
        });

        x += colWidths[i];
      }

      return roundedRowHeight;
    }

    let pageIndex = 0;
    const headerHeight = getRowHeight(headers, true);

    function addPageWithTemplate() {
      const page = pdfDoc.addPage([embeddedTemplate.width, embeddedTemplate.height]);
      page.drawPage(embeddedTemplate);
      drawHeader(page, pageIndex++);
      return page;
    }

    let page = addPageWithTemplate();
    let y = page.getHeight() - topMargin;

    drawStyledRow(headers, page, y, true);
    y -= headerHeight;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rowHeightUsed = getRowHeight(row);

      if (y - rowHeightUsed < bottomMargin) {
        page = addPageWithTemplate();
        y = page.getHeight() - topMargin;

        drawStyledRow(headers, page, y, true);
        y -= headerHeight;
      }

      drawStyledRow(row, page, y, false, i % 2 === 1);
      y -= rowHeightUsed;
    }

    const pages = pdfDoc.getPages();
    for (let i = 0; i < pages.length; i++) {
      drawFooter(pages[i], i, pages.length);
    }

    const fileName = `horario_${this.selectedStopText.replace(/\s+/g, '_').replace(/[^\w-]/g, '').trim()}_${this.day}.pdf`;
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  updateRealTime(): void {
    this.updateHoursMinutes();

    setInterval(() => {
      this.updateHoursMinutes();
    }, 1000);
  }

  updateHoursMinutes(): void {
    const agora = new Date();
    let hora = agora.getHours();
    let minutos = agora.getMinutes();

    if(hora != this.hora || minutos != this.minutos){
      this.recheckStopScheduleDay();
    }

    this.hora = hora;
    this.minutos = minutos;
  }

  getUserLocation() {
    this.geolocationService.getLocation()
      .then(position => {

        let lat = position.coords.latitude;
        let lng = position.coords.longitude;

        let distance = this.geolocationService.getDistanceInKm(
          lat, lng,
          this.center_orig['lat'], this.center_orig['lng']
        )

        if (distance < 30){
          this.center['lat'] = lat;
          this.center['lng'] = lng;
        }

        this.getMunicipalities();
      })
      .catch(err => {
        this.error = typeof err === 'string' ? err : err.message;
        this.getMunicipalities();
      });
  }

  getContent(){
    this.cmsService.getContentList(this.stops_content, this.language).subscribe(
      {
        next: data => {
          this.stopsInfo = data['body']['data'];
        },
        error: er => {
          this.router.navigate(['/500']);
        }
      }
    );
  }

  getMunicipalities(){
    this.externalService.getMunicipalities().subscribe(
      {
        next: data => {

          this.municipalities.splice(0)

          data.forEach(element => {
            let mun = {
              name: element['pDescricao'],
              code: element['pDescricao'],
              key: element['pKey'],
              region: element['pNUTIII']
            }

            this.municipalities.push(mun)
          });

          this.municipalities = this.municipalities.sort((a: any, b: any) => a.name.localeCompare(b.name, 'pt', { sensitivity: 'base' }) );

          this.getStops()
        },
        error: er => {
        this.router.navigate(['/500']);
        }
      })
  }

  getStops(){
    this.searching = true;

    this.externalService.getStops().subscribe(
      {
        next: data => {
          this.searching = false;

          this.stops = []

          const bounds = new LngLatBounds();
          const localStops: Stop[] = [];

          for (let i = 0; i < Math.min(10000, data.length); i++) {
            const item = data[i];

            const lat = Number(item['lat']);
            const lng = Number(item['lon']);

            const stop = {
              id: '1:' + item['stop_id'],
              code: item['stop_id'],
              name: item['stop_name'],
              municipality: '',
              lat: item['lat'],
              lng: item['lon'],
              zone: item['zone_id']
            };

            bounds.extend([lng, lat]);

            localStops.push(stop);
          }

          this.stops = [...localStops].sort((a, b) => a.name.localeCompare(b.name, 'pt', { sensitivity: 'base' }));
          this.stopsFiltered = this.stops;
          this.stopsFilteredMap = this.stops;

          setTimeout(() => {
            this.fitMapToStops();
          }, 500);

          this.bounds = bounds;

          setTimeout(() => {
            this.setStopsForMap(bounds);
            if(this.stopFromUrl){
              this.selectStop(this.stopFromUrl, false, true)
              this.router.navigate([], {
                queryParams: {},
                replaceUrl: true, // updates URL without adding to history
              });
            }
          }, 500);
        },
        error: er => {
          this.router.navigate(['/500']);
        }
      }
    )
  }

  sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async copyInBatches<T>(
    source: T[],
    target: T[],
    batchSize: number,
    delayMs: number
  ) {
    for (let i = 0; i < source.length; i += batchSize) {
      const batch = source.slice(i, i + batchSize);
      target.push(...batch);

      // Wait only if there are more items to copy
      if (i + batchSize < source.length) {
        await this.sleep(delayMs);
      }
    }
  }

  setStopsForMap(bounds: any){
    this.stopsFilteredMap = []

    if (!bounds && this.map) {
      bounds = this.map.getBounds();
    }

    if (!bounds) {
      return;
    }

    this.stopsFiltered.forEach((stop) => {
      const position = { lng: Number(stop.lng), lat: Number(stop.lat) };

      if (bounds.contains(position)) {
        this.stopsFilteredMap.push(stop);
      }
    });
  
  }

  getRoutes(){
    this.externalService.getRoutes().subscribe(
      {
        next: data => {
          this.allRoutes = data;
        },
        error: er => {
          this.router.navigate(['/500']);
        }
      }
    )
  }

  setDateRange(): void {
    const today = new Date();
    this.minDate.setDate(today.getDate() + 2);
    this.maxDate.setDate(today.getDate() + 30);
  }

  configureDatepicker(): void {
    this.bsConfig = {
      dateInputFormat: 'DD-MM-YYYY',
      showWeekNumbers: false,
      isAnimated: true,
      minDate: this.minDate,
      maxDate: this.maxDate,
      dateCustomClasses: this.generateCustomClasses()
    };
  }

  generateCustomClasses(): Array<{ date: Date; classes: string[] }> {
    const customClasses: Array<{ date: Date; classes: string[] }> = [];
    const today = new Date();
    for (let i = 2; i <= 30; i++) {
      const customDate = new Date(today);
      customDate.setDate(today.getDate() + i);
      customClasses.push({
        date: customDate,
        classes: ['dateBold']
      });
    }
    return customClasses;
  }

  onChanges(){
    this.searchForm.get('municipality')?.valueChanges.subscribe(
      val => {
        this.selectedStopData = undefined
        this.ignoreChanges = true;
        this.searchForm.get('text')?.setValue('');
        this.ignoreChanges = false;
        this.searchStops('mun');
      }
    )

    this.searchForm.get('text')?.valueChanges.subscribe(
      val => {
        if (this.ignoreChanges) return;
        this.selectedStopData = undefined
        this.searchStops('text');
        setTimeout(() => {
          this.TabsContentRef.scrollToIndex(0);
        },0)
      }
    )
  }

  get f(): { [key: string]: AbstractControl } {
    return this.searchForm.controls;
  }

  searchStops(type: string){
    this.selectedStop = '';
    this.selectedStopText = '';
    this.searching = true;

    if (type == 'mun') {
      let municipality = this.searchForm.get('municipality')?.value.toUpperCase();
      let text = this.searchForm.get('text')?.value.trim().toUpperCase();

      if(municipality != ''){
        this.externalService.getStopsByMunicipality(municipality).subscribe(
          {
            next: data => {
              this.stops = [];
              this.stopsFiltered = [];

              const bounds = new LngLatBounds();
              const localStops: Stop[] = [];

              for (let i = 0; i < Math.min(10000, data.length); i++) {
                const item = data[i];

                const lat = Number(item['lat']);
                const lng = Number(item['lon']);

                const stop = {
                  id: '1:' + item['stop_id'],
                  code: item['stop_id'],
                  name: item['stop_name'],
                  municipality: '',
                  lat: item['lat'],
                  lng: item['lon'],
                  zone: item['zone_id']
                };

                localStops.push(stop);

                bounds.extend([lng, lat]);
              }

              this.stops = [...localStops].sort((a, b) => a.name.localeCompare(b.name, 'pt', { sensitivity: 'base' }));;
              this.stopsFiltered = this.stops
              this.stopsFilteredMap = this.stops

              setTimeout(() => {
                this.map.resize();
                this.fitMapToStops();
              }, 500);

              this.searching = false;

              setTimeout(() => this.setStopsForMap(bounds), 500);

            },
            error: er => {
              this.router.navigate(['/500']);
            }
          }
        )
      } else {
        this.getStops();
      }

    } else {

      let text = this.searchForm.get('text')?.value.trim().toUpperCase();
      this.stopsFiltered = this.stops.filter((stop) => stop['name'].toUpperCase().includes(text))
      this.stopsFilteredMap = this.stops.filter((stop) => stop['name'].toUpperCase().includes(text))

      this.searching = false;
    }

  }

  selectStop(stop: string, scroll: boolean = false, scrollToSchedule: boolean = false){
    if(this.searchingSchedule == false){
      this.showPast = false;

      this.selectedStop = stop;

      let selStop = this.stopsFiltered.find((stopInfo) => stopInfo['id'] == stop);
      let NselStop = this.stopsFiltered.indexOf(selStop)
      if(selStop){
        this.selectedStopText = selStop['code'] + ': ' + selStop['name'] + ' (' + this.stringZone + ': ' + selStop['zone'] + ')';
      } else {
        this.selectedStopText = '';
      }

      this.selectedStopData = selStop;

      if (this.selectedStopData && this.map) {
        this.map.flyTo({
          center: [Number(this.selectedStopData.lng), Number(this.selectedStopData.lat)],
          zoom: 15,
          speed: 1.5,
          curve: 1
        });
      }

      this.getStopScheduleDay(this.selectedStop, this.selectedDay, scrollToSchedule)

      if(scroll) this.scrollToStop(NselStop);
    }
  }

  changeDateToday(buttonId: string){
    if(this.searchingSchedule == false){
      this.showPast = false
      this.activeButton = buttonId;
      this.selectedDay = this.today;
      this.getStopScheduleDay(this.selectedStop, this.today)
    }
  }

  changeDateTomorrow(buttonId: string){
    if(this.searchingSchedule == false){
      this.showPast = false
      this.activeButton = buttonId;
      this.selectedDay = this.tomorrow;
      this.getStopScheduleDay(this.selectedStop, this.tomorrow)
    }
  }

  onDateChange(date: Date, buttonId: string){
    this.showPast = false

    if (this.isInitialLoad) {
      this.isInitialLoad = false;
      return;
    }

    this.activeButton = 'datepicker';
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    this.selectedDay = `${year}${month}${day}`;
    this.getStopScheduleDay(this.selectedStop, this.selectedDay)
  }

  getStopScheduleDay(stop: string,day: string, scrollToSchedule: boolean = false){
    this.searchingSchedule = true;
    if(stop != '' && day != ''){
      this.loadingStopInfo = 0;
      this.stopSchedule = [];
      this.results = [];

      let stop_info = this.stopsFiltered.find((stopInfo) => stopInfo['id'] == stop);
      if(stop_info){
        this.center = {
          lat: stop_info['lat'],
          lng: stop_info['lng'],
        };
      } else {
        this.center = {
          lat: environment.mapCenter.lat,
          lng: environment.mapCenter.lon,
        };
      }

      this.day = day.slice(0, 4) + '/' + day.slice(4, 6) + '/' + day.slice(6, 8);

      this.isToday = this.utilsService.getTodayDate() == day;

      this.externalService.getStopScheduleDay(stop,day).subscribe(
        {
          next: data => {
            this.results.push(data);

            for (let i = 0; i < this.results[0].length; i++) {
              let id = this.results[0][i]['pattern']['id']
              let line = this.results[0][i]['pattern']['desc']
                .replaceAll(' to ', ' para ')
                .replaceAll(' from ', ' de ')
                .replaceAll('1:', '')

              let route_id = this.results[0][i]['pattern']['id'].split(':')[0]+':'+this.results[0][i]['pattern']['id'].split(':')[1]

              let route = this.allRoutes.find((route) => route['id'] == route_id);

              let line_name: string
              let short_name: string
              let long_name: string
              let color: string

              if(route == undefined){
                line_name = id.split(':')[1]
                short_name = id.split(':')[1]
                long_name = ''
                color = '#FFFFFF'
              } else {
                line_name = route['shortName'] + ' - ' + route['longName']
                short_name = route['shortName']
                long_name = route['longName']
                color = '#' + route['color']
              }

              if (this.results[0][i]['times'].length > 0) {
                this.results[0][i]['times'].forEach((element: any) => {
                  let time = {
                    line: line_name,
                    headsign: line,
                    realtimeArrival: element['realtimeArrival'],
                    realtimeArrivalDesc: this.utilsService.secondsToTime(element['realtimeArrival']),
                    realtimeState: element['realtimeState'],
                    routeShortName: short_name,
                    routeLongName: long_name,
                    routeColor: color,
                    past: this.isToday && (element['realtimeArrival'] < this.utilsService.getMillisecondsOfCurrentDay()/1000 || element['realtimeArrival'] == undefined)
                  }

                  this.stopSchedule.push(time)
                });
              }

            }

            this.stopSchedule.sort((a,b) => (a.realtimeArrival > b.realtimeArrival) ? 1 : -1 )

            this.showButton = this.stopSchedule.filter(item => item.past).length > 1 && this.isToday
            this.showMyHour = this.stopSchedule.length > -0 && this.isToday

            this.stopSchedulePastTrue = this.stopSchedule.filter(item => item.past).slice(-1)
            this.stopSchedulePastFalse = this.stopSchedule.filter(item => !item.past)

            this.searchingSchedule = false;
            this.loadingStopInfo++;

            if(scrollToSchedule) {
              setTimeout(() => {
                this.targetSection.nativeElement.scrollIntoView({ behavior: 'smooth' });
              }, 0);
            }

          },
          error: er => {
            this.searchingSchedule = false;
            this.loadingStopInfo++;
          }
        }
      )
    }
  }

  recheckStopScheduleDay(){
    if(this.selectedStop != undefined && this.selectedStop != ''){
      this.stopSchedule.forEach((stop) => {
        stop.past = this.isToday && (stop.realtimeArrival < this.utilsService.getMillisecondsOfCurrentDay()/1000 || stop.realtimeArrival == undefined)
      })

      if (this.showPast){
        this.stopSchedulePastTrue = this.stopSchedule.filter(item => item.past)
      } else {
        this.stopSchedulePastTrue = this.stopSchedule.filter(item => item.past).slice(-1)
      }
      
      this.stopSchedulePastFalse = this.stopSchedule.filter(item => !item.past)
    }
      
  }

  isFormValid(): boolean {
    const municipalityControl = this.searchForm.get('municipality');
    const textControl = this.searchForm.get('text');
    return municipalityControl?.value !== '' || textControl?.value !== '';
  }

  onReset(): void {
    this.searchForm.get('municipality')?.setValue('');
    //this.searchForm.get('text')?.setValue('');
  }

  scrollToStop(id: number): void {
    this.TabsContentRef.scrollToIndex(id, 'instant')
    setTimeout(() => {
          this.TabsContentRef.scrollToIndex(id, 'instant')
    },0)
  }

  toggleShowPast(){
    this.showPast = !this.showPast;
    if (this.showPast){
      this.stopSchedulePastTrue = this.stopSchedule.filter(item => item.past)
    } else {
      this.stopSchedulePastTrue = this.stopSchedule.filter(item => item.past).slice(-1)
    }
    
  }


  /* Mapbox Methods */
  
  onMapLoad(event: any) {
    //console.log('💸 NEW BILLING SESSION STARTED 💸');
    this.map = event.target ? event.target : event;
    
    const nav = new mapboxgl.NavigationControl({
      showCompass: false,
      showZoom: true,
      visualizePitch: false
    });
    this.map.addControl(nav, 'bottom-right');
    
    const geolocate = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true
    });
    this.map.addControl(geolocate, 'bottom-right');

    this.map.flyTo({
      center: [Number(this.center.lng), Number(this.center.lat)],
      zoom: 8,
      speed: 10,
      curve: 1
    });
  }

  fitMapToStops() {
    if (!this.map || !this.stopsFilteredMap || this.stopsFilteredMap.length === 0) {
      return;
    }

    const bounds = new LngLatBounds();

    this.stopsFilteredMap.forEach(stop => {
      bounds.extend([Number(stop.lng), Number(stop.lat)]);
    });

    this.map.fitBounds(bounds, {
      padding: 20, 
      maxZoom: 30,
      duration: 500 
    });
  }

  get pointsGeoJson(): GeoJSON.FeatureCollection {
    return {
      type: 'FeatureCollection',
      features: this.stopsFilteredMap.map(stop => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [Number(stop.lng), Number(stop.lat)]
        },
        properties: {
          id: stop.id,
          name: stop.name,
        }
      }))
    };
  }

  cursorStyle = '';

  onPointClick(event: any) {
    const properties = event.features[0].properties;
    
    this.selectStop(properties.id, true);
  }

  onClusterClick(event: any) {
    const feature = event.features[0];
    const clusterId = feature.properties.cluster_id;
    const source = this.map.getSource('points-source') as GeoJSONSource;

    source.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err || zoom === null) return;

      this.map.easeTo({
        center: feature.geometry.coordinates,
        zoom: zoom,
        duration: 500
      });
    });
  }

  onZoomChanged() {
    if (!this.map) return;

    const zoom = this.map.getZoom();

    if (zoom !== undefined && zoom !== null) {
      this.zoom = zoom;
    }
  }

  changeStyle(layer: 'streets' | 'satellite') {
    this.currentStyle = layer;
    
    if (layer === 'streets') {
      this.mapStyle = 'mapbox://styles/mapbox/streets-v12';
    } else {
      // Satellite with Streets (Hybrid)
      this.mapStyle = 'mapbox://styles/mapbox/satellite-streets-v12'; 
    }
  }

}
